import { gsap } from "../../animations/gsapSetup.ts";
import { DeviceMockup } from "../../components/common/DeviceMockup.tsx";
import { useGsapScrollTrigger } from "../../hooks/useGsapScrollTrigger.ts";
import { useIsMobile } from "../../hooks/useMediaQuery.ts";
import { useReducedMotion } from "../../hooks/useReducedMotion.ts";
import { useI18n } from "../../i18n/context.tsx";
import styles from "./VoiceControl.module.css";

const BAR_HEIGHTS = [16, 32, 24, 40];

export function VoiceControl() {
  const reducedMotion = useReducedMotion();
  const isMobile = useIsMobile();
  const { t } = useI18n();

  const ref = useGsapScrollTrigger(
    (el) => {
      if (reducedMotion) return;

      const bars = el.querySelectorAll(`.${styles.bar}`);
      const textContent = el.querySelector(`.${styles.textContent}`);
      const mockup = el.querySelector(`.${styles.mockupWrap}`);

      if (isMobile) {
        gsap.from([mockup, textContent], {
          y: 40,
          opacity: 0,
          duration: 0.8,
          stagger: 0.2,
          scrollTrigger: {
            trigger: el,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        });
      } else {
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: el,
            pin: true,
            scrub: 1,
            start: "top top",
            end: "+=150%",
          },
        });

        tl.from(mockup, {
          x: -80,
          opacity: 0,
          duration: 1,
        })
          .from(
            textContent,
            {
              x: 80,
              opacity: 0,
              duration: 1,
            },
            "<0.2",
          )
          .from(
            bars,
            {
              scaleY: 0,
              opacity: 0,
              duration: 0.6,
              stagger: 0.1,
            },
            "-=0.4",
          );
      }

      gsap.to(bars, {
        scaleY: 1.5,
        duration: 0.6,
        stagger: {
          each: 0.15,
          repeat: -1,
          yoyo: true,
        },
        ease: "sine.inOut",
      });
    },
    [isMobile],
  );

  return (
    <section ref={ref} className={styles.section}>
      <div className={styles.inner}>
        <div className={styles.mockupWrap}>
          <DeviceMockup device="iphone">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: "var(--color-gray-500)",
                fontSize: "var(--text-sm)",
              }}
            >
              Voice UI Preview
            </div>
          </DeviceMockup>
        </div>
        <div className={styles.textContent}>
          <h2 className={styles.title}>{t.classic.voiceTitle}</h2>
          <p className={styles.description}>{t.classic.voiceDesc}</p>
          <div className={styles.soundWave}>
            {BAR_HEIGHTS.map((height) => (
              <div key={`bar-${height}`} className={styles.bar} style={{ height: `${height}px` }} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
